import type { MigrationTier } from "@wp-to-emdash/core";
import type {
  ApiAuditResponse,
  ApiCheckoutResponse,
  ApiCreateJobResponse,
  ApiFromCheckoutResponse,
  ApiJobDetailResponse,
  ApiJobListResponse,
  ApiJobOutputResponse,
  ApiJobReportResponse,
  ApiMagicLinkResponse,
  ApiRefreshResponse,
  ApiVerifyResponse,
} from "@/types/api";
import { getToken, clearToken } from "./auth";

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

class ApiClient {
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_URL || "";
  }

  private getHeaders(includeAuth: boolean): HeadersInit {
    const headers: Record<string, string> = {};
    if (includeAuth) {
      const token = getToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    return headers;
  }

  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    const isFormData = options?.body instanceof FormData;

    const headers: Record<string, string> = {
      ...this.getHeaders(true),
    } as Record<string, string>;

    // Let the browser set the multipart boundary for FormData
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers: {
        ...headers,
        ...(options?.headers as Record<string, string> | undefined),
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearToken();
      }

      let message = `Request failed with status ${response.status}`;
      try {
        const body = (await response.json()) as { error?: string };
        if (body.error) {
          message = body.error;
        }
      } catch {
        // body wasn't JSON — keep the default message
      }

      throw new ApiRequestError(response.status, message);
    }

    return (await response.json()) as T;
  }

  // --- Auth endpoints (no JWT required) ---

  async requestMagicLink(email: string): Promise<ApiMagicLinkResponse> {
    return this.request<ApiMagicLinkResponse>("/auth/magic-link", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async verifyMagicLink(token: string): Promise<ApiVerifyResponse> {
    return this.request<ApiVerifyResponse>(`/auth/verify?token=${encodeURIComponent(token)}`);
  }

  async authFromCheckout(sessionId: string): Promise<ApiFromCheckoutResponse> {
    return this.request<ApiFromCheckoutResponse>("/auth/from-checkout", {
      method: "POST",
      body: JSON.stringify({ sessionId }),
    });
  }

  async refreshToken(): Promise<ApiRefreshResponse> {
    return this.request<ApiRefreshResponse>("/auth/refresh", {
      method: "POST",
    });
  }

  // --- Free endpoints ---

  async audit(file: File): Promise<ApiAuditResponse> {
    const form = new FormData();
    form.append("wxr", file);
    return this.request<ApiAuditResponse>("/audit", {
      method: "POST",
      body: form,
    });
  }

  // --- Auth-required endpoints ---

  async createCheckout(
    tier: MigrationTier,
    returnUrl: string,
  ): Promise<ApiCheckoutResponse> {
    return this.request<ApiCheckoutResponse>("/jobs/checkout", {
      method: "POST",
      body: JSON.stringify({ tier, returnUrl }),
    });
  }

  async createJob(
    wxrFile: File,
    checkoutSessionId: string,
    sourceUrl?: string,
  ): Promise<ApiCreateJobResponse> {
    const form = new FormData();
    form.append("wxr", wxrFile);
    form.append("checkout_session_id", checkoutSessionId);
    if (sourceUrl) {
      form.append("source_url", sourceUrl);
    }
    return this.request<ApiCreateJobResponse>("/jobs", {
      method: "POST",
      body: form,
    });
  }

  async listJobs(): Promise<ApiJobListResponse> {
    return this.request<ApiJobListResponse>("/jobs");
  }

  async getJob(id: string): Promise<ApiJobDetailResponse> {
    return this.request<ApiJobDetailResponse>(`/jobs/${encodeURIComponent(id)}`);
  }

  async getJobReport(id: string): Promise<ApiJobReportResponse> {
    return this.request<ApiJobReportResponse>(
      `/jobs/${encodeURIComponent(id)}/report`,
    );
  }

  async getJobOutput(id: string): Promise<ApiJobOutputResponse> {
    return this.request<ApiJobOutputResponse>(
      `/jobs/${encodeURIComponent(id)}/output`,
    );
  }

  async downloadJob(id: string): Promise<Response> {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(
      `${this.baseUrl}/jobs/${encodeURIComponent(id)}/download`,
      { headers },
    );

    if (!response.ok) {
      if (response.status === 401) {
        clearToken();
      }
      let message = `Download failed with status ${response.status}`;
      try {
        const body = (await response.json()) as { error?: string };
        if (body.error) {
          message = body.error;
        }
      } catch {
        // binary response or non-JSON error
      }
      throw new ApiRequestError(response.status, message);
    }

    return response;
  }
}

export const api = new ApiClient();
